import * as React from 'react';
import { Icon } from '../icons.jsx';
import { Button, Placeholder, ProviderPhoto, Field, Input, Select, Modal } from '../ui.jsx';
import { AGE_GROUPS } from '../data';
import { api } from '../api';
import { useApi, useAsyncAction } from '../hooks';
import {
  buildTourDates,
  formatTourDateLabel,
  getAvailableTourTimes,
  getNoticeForDate,
  isTourDateClosed,
} from '../calendar';

// Tour Booking flow (modal)

const StepDots = ({ step, total }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    {Array.from({ length: total }).map((_, i) => (
      <div key={i} style={{
        width: i === step ? 22 : 8, height: 8, borderRadius: 999,
        background: i <= step ? 'var(--brand)' : 'var(--bg-2)',
        transition: 'all .2s ease',
      }}/>
    ))}
  </div>
);

const TourTypeButton = ({ icon, title, sub, selected, onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: selected ? 'var(--brand-tint)' : 'var(--surface)',
      border: `1.5px solid ${selected ? 'var(--brand)' : 'var(--line-2)'}`,
      borderRadius: 'var(--r-md)',
      padding: 20, cursor: 'pointer',
      textAlign: 'left', flex: 1, fontFamily: 'inherit',
      transition: 'all .15s ease',
      boxShadow: selected ? '0 0 0 4px rgba(26,102,213,0.14)' : 'none',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{
        width: 44, height: 44, borderRadius: 12, flexShrink: 0,
        background: selected ? 'var(--brand)' : 'var(--brand-tint)',
        color: selected ? '#fff' : 'var(--brand)',
        display: 'grid', placeItems: 'center',
      }}>
        <Icon name={icon} size={20}/>
      </span>
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', lineHeight: 1.25 }}>{title}</div>
    </div>
    <div style={{ fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.5 }}>{sub}</div>
  </button>
);

const DAY_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

const DateCell = ({ date, selected, onClick }) => {
  const day = DAY_SHORT[date.getDay()];
  const num = date.getDate();
  const closed = isTourDateClosed(date);
  return (
    <button
      onClick={onClick}
      disabled={closed}
      aria-label={`${formatTourDateLabel(date)}${closed ? ', closed' : ''}`}
      style={{
        padding: '10px 4px', borderRadius: 12,
        background: selected ? 'var(--brand)' : 'var(--surface)',
        color: selected ? '#fff' : closed ? 'var(--ink-4)' : 'var(--ink)',
        border: `1px solid ${selected ? 'var(--brand)' : 'var(--line-2)'}`,
        cursor: closed ? 'not-allowed' : 'pointer',
        opacity: closed ? 0.45 : 1,
        fontFamily: 'inherit',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        transition: 'all .12s ease',
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{day}</span>
      <span style={{ fontSize: 17, fontWeight: 700 }}>{num}</span>
    </button>
  );
};

const TimeChip = ({ label, selected, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: '10px 14px', borderRadius: 999,
      background: selected ? 'var(--brand)' : 'var(--surface)',
      color: selected ? '#fff' : 'var(--ink)',
      border: `1px solid ${selected ? 'var(--brand)' : 'var(--line-2)'}`,
      cursor: 'pointer', fontSize: 13, fontWeight: 600,
      fontFamily: 'inherit',
      transition: 'all .12s ease',
    }}
  >{label}</button>
);

export const BookingModal = ({ open, onClose, providerId, initialTourType = 'in-person' }) => {
  // Fetch the active provider through the API seam so the modal stays correct
  // once a real backend is serving providers not present in the mock dataset.
  const { data: provider, loading: providerLoading, error: providerError } = useApi(
    () => (providerId ? api.providers.get(providerId) : Promise.resolve(null)),
    [providerId],
  );
  const { data: notices, error: noticesError } = useApi(
    () => (providerId ? api.notices.list(providerId) : Promise.resolve([])),
    [providerId],
  );
  const p = provider || {
    id: providerId || 'unknown',
    name: providerLoading ? 'Loading\u2026' : 'Childcare provider',
    ages: [],
    tone: 'A',
    image: null,
  };
  const [step, setStep] = React.useState(0);
  const [tourType, setTourType] = React.useState(initialTourType);
  const [date, setDate] = React.useState(null);
  const [time, setTime] = React.useState(null);
  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [age, setAge] = React.useState('Toddler');
  const bookingAction = useAsyncAction((booking) => api.bookings.create(booking));

  React.useEffect(() => {
    if (open) {
      setStep(0); setTourType(initialTourType);
      setDate(null); setTime(null);
      setName(''); setPhone('');
      setAge((provider && provider.ages && provider.ages[0]) || 'Toddler');
    }
  }, [open, providerId, initialTourType, provider]);

  const dates = React.useMemo(() => buildTourDates(new Date()), [open]);
  const availableTimes = date ? getAvailableTourTimes(date, notices || []) : [];
  const selectedNotice = date ? getNoticeForDate(date, notices || []) : null;
  const phoneOk = phone.replace(/\D/g, '').length >= 10;
  const stepValid = [true, !!(date && time), !!(name && phoneOk), true][step];

  const formatPhone = (v) => {
    const d = v.replace(/\D/g, '').slice(0, 10);
    if (d.length < 4) return d;
    if (d.length < 7) return `(${d.slice(0,3)}) ${d.slice(3)}`;
    return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  };

  const dateLabel = date ? formatTourDateLabel(date) : '';
  const summaryLine = date && time ? `${dateLabel} at ${time}` : '';
  const submitBooking = async () => {
    if (!date || !time || !providerId) return;
    try {
      await bookingAction.run({
        providerId,
        parentName: name,
        childAge: age,
        tourType,
        date: dateLabel,
        time,
        phone,
      });
      setStep(3);
    } catch {
      // useAsyncAction exposes the error above the booking steps.
    }
  };

  return (
    <Modal open={open} onClose={onClose} width={580} ariaLabel={`Book a tour at ${p.name}`}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 24px', borderBottom: '1px solid var(--line)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {p.image
            ? <ProviderPhoto src={p.image} alt="" radius={10} style={{ width: 42, height: 42 }}/>
            : <Placeholder tone={p.tone} radius={10} style={{ width: 42, height: 42 }} label="" iconSize={22}/>}
          <div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              {step === 3 ? 'Tour booked' : 'Book a tour at'}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginTop: 2 }}>{p.name}</div>
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            background: 'var(--bg-2)', border: 'none', borderRadius: 999,
            width: 34, height: 34, cursor: 'pointer',
            display: 'grid', placeItems: 'center', color: 'var(--ink-2)',
          }}
        >
          <Icon name="x" size={16}/>
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: 28, overflowY: 'auto' }}>
        {providerError && (
          <div role="alert" style={{
            marginBottom: 16, padding: '10px 12px', borderRadius: 10, fontSize: 13,
            color: '#7A1F18', background: 'var(--coral-soft)', border: '1px solid rgba(226,50,38,0.2)',
          }}>
            We couldn&rsquo;t load the latest details for this center: {providerError.message}.
          </div>
        )}
        {bookingAction.error && (
          <div role="alert" style={{
            marginBottom: 16, padding: '10px 12px', borderRadius: 10, fontSize: 13,
            color: '#7A1F18', background: 'var(--coral-soft)', border: '1px solid rgba(226,50,38,0.2)',
          }}>
            We couldn&rsquo;t send this tour request: {bookingAction.error.message}.
          </div>
        )}
        {noticesError && (
          <div role="alert" style={{
            marginBottom: 16, padding: '10px 12px', borderRadius: 10, fontSize: 13,
            color: '#7A5210', background: 'var(--wait-soft)', border: '1px solid rgba(176,122,28,0.2)',
          }}>
            We couldn&rsquo;t check this center&rsquo;s special hours. The center will verify the
            requested time before confirming.
          </div>
        )}
        {step < 3 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <span style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600 }}>
              Step {step + 1} of 3
            </span>
            <StepDots step={step} total={3}/>
          </div>
        )}

        {/* STEP 0 - Tour type */}
        {step === 0 && (
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 6 }}>
              How would you like to visit?
            </h2>
            <p style={{ color: 'var(--ink-3)', fontSize: 14, marginBottom: 22 }}>
              Both tour types are free, and most last about 30 minutes.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <TourTypeButton
                icon="video" title="Virtual Tour"
                sub="Live video walkthrough · no travel"
                selected={tourType === 'virtual'}
                onClick={() => setTourType('virtual')}
              />
              <TourTypeButton
                icon="calendar-check" title="In Person"
                sub="Visit the center · meet teachers"
                selected={tourType === 'in-person'}
                onClick={() => setTourType('in-person')}
              />
            </div>

            <div style={{ marginTop: 22 }}>
              <Field id="booking-age-group" label="Which age group is this tour for?">
                <Select
                  icon="user"
                  value={age} onChange={setAge}
                  options={AGE_GROUPS}
                />
              </Field>
            </div>
          </div>
        )}

        {/* STEP 1 - Date + Time */}
        {step === 1 && (
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 6 }}>
              Pick a date and time
            </h2>
            <p style={{ color: 'var(--ink-3)', fontSize: 14, marginBottom: 18 }}>
              All times shown in Eastern. The center will confirm by text.
            </p>

            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 22,
            }}>
              {dates.map((d, i) => (
                <DateCell
                  key={i}
                  date={d}
                  selected={date && d.toDateString() === date.toDateString()}
                  onClick={() => {
                    setDate(d);
                    setTime(null);
                  }}
                />
              ))}
            </div>

            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-3)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>
              {date ? `Available on ${formatTourDateLabel(date)}` : 'Pick a date first'}
            </div>
            {selectedNotice && (
              <div className="booking-special-hours">
                <Icon name="bell" size={15}/>
                <span>{selectedNotice.message}</span>
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, opacity: date ? 1 : 0.4 }}>
              {availableTimes.map(t => (
                <TimeChip
                  key={t}
                  label={t}
                  selected={time === t}
                  onClick={() => date && setTime(t)}
                />
              ))}
              {date && availableTimes.length === 0 && (
                <div className="booking-no-times">
                  No tour times are available on this date. Please choose another day.
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 2 - Contact */}
        {step === 2 && (
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 6 }}>
              Just your name and phone
            </h2>
            <p style={{ color: 'var(--ink-3)', fontSize: 14, marginBottom: 22 }}>
              We&rsquo;ll text you the tour details. No email, no account.
            </p>

            <div style={{
              background: 'var(--brand-tint)', borderRadius: 12, padding: '12px 14px',
              fontSize: 13, color: 'var(--brand-ink)', marginBottom: 22,
              display: 'flex', gap: 10, alignItems: 'flex-start',
            }}>
              <Icon name="calendar-check" size={16} style={{ marginTop: 1, flexShrink: 0 }}/>
              <span>
                <strong>{tourType === 'virtual' ? 'Virtual tour' : 'In-person tour'}</strong>
                {' '}for {age.toLowerCase()} · {summaryLine}
              </span>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <Field id="booking-name" label="Your name">
                <Input
                  icon="user"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Alex Morgan"
                />
              </Field>
              <Field id="booking-phone" label="Phone number" hint="We only use this for tour reminders.">
                <Input
                  icon="phone"
                  value={phone}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                  placeholder="(215) 555-0123"
                  inputMode="tel"
                />
              </Field>
            </div>
          </div>
        )}

        {/* STEP 3 - Confirmation */}
        {step === 3 && (
          <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
            <div style={{
              width: 64, height: 64, borderRadius: 999, margin: '0 auto 22px',
              background: 'var(--brand-tint)', display: 'grid', placeItems: 'center',
              color: 'var(--brand)',
            }}>
              <Icon name="check" size={28} strokeWidth={2.4}/>
            </div>
            <h2 style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 8 }}>
              Your request is on its way!
            </h2>
            <p style={{ color: 'var(--ink-3)', fontSize: 15, marginBottom: 24, maxWidth: 380, margin: '0 auto 24px' }}>
              The center will confirm by text. Once confirmed, you&rsquo;ll receive the tour details and a reminder.
            </p>

            <div style={{
              background: 'var(--surface-2)', border: '1px solid var(--line)',
              borderRadius: 'var(--r-md)', padding: 18, textAlign: 'left',
              maxWidth: 380, margin: '0 auto',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: 14, fontSize: 14 }}>
                <Icon name={tourType === 'virtual' ? 'video' : 'calendar-check'} size={16} style={{ color: 'var(--brand)', marginTop: 2 }}/>
                <div>
                  <div style={{ fontWeight: 700, color: 'var(--ink)' }}>
                    {tourType === 'virtual' ? 'Virtual tour' : 'In-person tour'}
                  </div>
                  <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>{p.name}</div>
                </div>
                <Icon name="calendar" size={16} style={{ color: 'var(--brand)', marginTop: 2 }}/>
                <div style={{ color: 'var(--ink-2)' }}>{summaryLine}</div>
                <Icon name="user" size={16} style={{ color: 'var(--brand)', marginTop: 2 }}/>
                <div style={{ color: 'var(--ink-2)' }}>{name} · {age}</div>
                <Icon name="phone" size={16} style={{ color: 'var(--brand)', marginTop: 2 }}/>
                <div style={{ color: 'var(--ink-2)' }}>{phone}</div>
              </div>
              <p style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--line)', color: 'var(--ink-3)', fontSize: 13, lineHeight: 1.5 }}>
                Need to reschedule? Reply STOP to the reminder text, or call the center directly.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div style={{
        padding: '16px 24px', borderTop: '1px solid var(--line)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'var(--surface-2)',
      }}>
        {step === 3 ? (
          <>
            <Button variant="ghost" onClick={onClose}>Add to calendar</Button>
            <Button variant="primary" onClick={onClose} iconRight="arrow-right">Done</Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={() => step === 0 ? onClose() : setStep(step - 1)}
              icon={step === 0 ? undefined : 'chev-left'}
            >
              {step === 0 ? 'Cancel' : 'Back'}
            </Button>
            <Button
              variant="primary"
              disabled={!stepValid || bookingAction.loading}
              onClick={() => step === 2 ? submitBooking() : setStep(step + 1)}
              iconRight={step === 2 ? 'check' : 'arrow-right'}
            >
              {step === 2
                ? bookingAction.loading
                  ? 'Sending…'
                  : 'Send request'
                : 'Continue'}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};
